import { type ReactElement } from 'react';
import { type CredentialRotationHistoryListItem } from '@anchorbase/pillar-sdk';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

export interface CredentialRotationHistoryTableProps {
    readonly attempts: readonly CredentialRotationHistoryListItem[];
    readonly onManualPromote?: (
        attempt: CredentialRotationHistoryListItem
    ) => void;
}

function formatTimestamp(value: string | null): string {
    return value ?? 'Not recorded';
}

function formatTargetFields(item: CredentialRotationHistoryListItem): string {
    const labels = item.display.target_fields.map((field) => field.label);
    return labels.length === 0 ? 'None' : labels.join(', ');
}

function renderHistoryRow(
    item: CredentialRotationHistoryListItem,
    onManualPromote:
        | ((attempt: CredentialRotationHistoryListItem) => void)
        | undefined
): ReactElement {
    const manualPromotionDisabledReason =
        item.capabilities.manual_promote_disabled_reason ??
        'You do not have permission to manually promote this rotation attempt.';
    const manualPromotionTitle = item.capabilities.can_manual_promote
        ? 'Confirm manual promotion'
        : manualPromotionDisabledReason;
    return (
        <TableRow key={item.attempt.credential_rotation_attempt_uuid}>
            <TableCell className="font-mono text-xs">
                {item.attempt.credential_rotation_attempt_uuid}
            </TableCell>
            <TableCell className="font-mono text-xs">
                {item.attempt.credential_uuid}
            </TableCell>
            <TableCell>{item.display.ipx_credential_definition_name}</TableCell>
            <TableCell>{item.display.account_label}</TableCell>
            <TableCell>{formatTargetFields(item)}</TableCell>
            <TableCell>
                {item.display.flow_display_name ?? item.attempt.flow_key}
            </TableCell>
            <TableCell>{item.attempt.state}</TableCell>
            <TableCell>{formatTimestamp(item.attempt.created_ts)}</TableCell>
            <TableCell>{formatTimestamp(item.attempt.injected_ts)}</TableCell>
            <TableCell>{formatTimestamp(item.attempt.submitted_ts)}</TableCell>
            <TableCell>{formatTimestamp(item.attempt.promoted_ts)}</TableCell>
            <TableCell>{formatTimestamp(item.attempt.failed_ts)}</TableCell>
            <TableCell>{formatTimestamp(item.attempt.expires_ts)}</TableCell>
            <TableCell>{item.attempt.evidence_kind ?? 'None'}</TableCell>
            <TableCell>
                {item.display.actor_role_assignment_label ?? 'System'}
            </TableCell>
            <TableCell>
                <span className="inline-flex" title={manualPromotionTitle}>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!item.capabilities.can_manual_promote}
                        title={manualPromotionTitle}
                        onClick={() => {
                            if (!item.capabilities.can_manual_promote) {
                                return;
                            }
                            onManualPromote?.(item);
                        }}
                    >
                        Confirm
                    </Button>
                </span>
            </TableCell>
        </TableRow>
    );
}

export function CredentialRotationHistoryTable(
    props: CredentialRotationHistoryTableProps
): ReactElement {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Attempt</TableHead>
                    <TableHead>Credential</TableHead>
                    <TableHead>Definition</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Fields</TableHead>
                    <TableHead>Flow</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Injected</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Promoted</TableHead>
                    <TableHead>Failed</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Evidence</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Action</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {props.attempts.map((attempt) =>
                    renderHistoryRow(attempt, props.onManualPromote)
                )}
            </TableBody>
        </Table>
    );
}
